import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createT, SupportedLanguage } from './strings';

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  locale: string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    const stored = localStorage.getItem('lang');
    return (stored === 'en' || stored === 'sv') ? (stored as SupportedLanguage) : 'sv';
  });

  useEffect(() => {
    try { localStorage.setItem('lang', language); } catch {}
  }, [language]);

  const setLanguage = (lang: SupportedLanguage) => setLanguageState(lang);

  const t = useMemo(() => createT(language), [language]);

  const locale = language === 'sv' ? 'sv-SE' : 'en-GB';

  const value = useMemo(() => ({ language, setLanguage, t, locale }), [language, setLanguage, t, locale]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
